import { API_BASE, authHeaders } from './apiAuth'

/**
 * Unsplash and Pexels, through the Worker (workers/api/src/routes/stock.ts),
 * for the Mood board page. The keys stay on the server.
 */

export type StockSource = 'unsplash' | 'pexels'

export interface StockPicture {
  id: string
  source: StockSource
  thumb: string
  url: string
  /** The whole picture at its own size, for what is kept, cut or sent (2026-10-16). Older Workers: absent. */
  full?: string
  width: number
  height: number
  title: string
  author: string
  authorUrl: string
  link: string
  downloadLocation?: string
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `HTTP ${response.status}`)
  }
  return (await response.json()) as T
}

/** Which libraries have a key set. Both false when the answer could not be had. */
export async function stockSources(): Promise<Record<StockSource, boolean>> {
  try {
    return await call<Record<StockSource, boolean>>('/api/stock/sources')
  } catch {
    return { unsplash: false, pexels: false }
  }
}

export function searchStock(
  source: StockSource,
  q: string,
  page = 1,
): Promise<{ total: number; more: boolean; pictures: StockPicture[] }> {
  return call(`/api/stock/search?source=${source}&q=${encodeURIComponent(q)}&page=${page}`)
}

/** Unsplash's rule: say once when a picture is actually used. Never throws. */
export async function markStockUsed(locations: string[]): Promise<void> {
  if (locations.length === 0) return
  try {
    await call('/api/stock/used', { method: 'POST', body: JSON.stringify({ locations }) })
  } catch {
    /* counting is their bookkeeping; a miss must not stop a send */
  }
}

/**
 * What the words mean in the library's own tags (routes/moodboardTerms.ts):
 * "happy" -> Joyful, Smiling, Bright... Empty when it could not be had, in
 * which case the words are searched as typed. Never throws; gives up after a
 * few seconds (3.5) so a slow answer does not hold the wall.
 */
const termsSeen = new Map<string, string[]>()

const termsAsked = new Map<string, Promise<string[]>>()

/**
 * `wait`: how long this caller will wait for the answer. The question is
 * asked once and keeps going after a caller gives up, so a later caller --
 * the same words with nothing found yet -- can wait longer for the same
 * answer (2026-10-12: "warm" came back as Nothing found while the answer was
 * still on its way).
 */
export function libraryTerms(text: string, wait = 3500): Promise<string[]> {
  // The same words twice (another source, back again): no second asking.
  const key = text.trim().toLowerCase()
  const known = termsSeen.get(key)
  if (known) return Promise.resolve(known)
  let asking = termsAsked.get(key)
  if (!asking) {
    asking = call<{ terms?: unknown }>('/api/moodboard/terms', { method: 'POST', body: JSON.stringify({ text }) })
      .then((answer) => {
        const terms = Array.isArray(answer?.terms) ? answer.terms.filter((t): t is string => typeof t === 'string') : []
        if (terms.length > 0) termsSeen.set(key, terms)
        else console.warn('[WonderWorks] no meaning came back for', text)
        return terms
      })
      .catch((error: unknown) => {
        console.warn('[WonderWorks] the meaning of the words could not be had', error)
        return [] as string[]
      })
      .finally(() => termsAsked.delete(key))
    termsAsked.set(key, asking)
  }
  return Promise.race([asking, new Promise<string[]>((resolve) => setTimeout(() => resolve([]), wait))])
}
